"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, X } from "lucide-react";

// "Find a laptop" stays ahead of "All laptops": the questionnaire is the product,
// browsing is the way in for people who already have a model in mind.
const NAV_LINKS = [{
  href: "/about",
  label: "About"
}, {
  href: "/blog",
  label: "Blog"
}, {
  href: "/recommend",
  label: "Find a laptop"
}, {
  href: "/laptops",
  label: "All laptops"
}, {
  href: "/compare",
  label: "Compare"
}, {
  href: "/extension",
  label: "Amazon extension"
}, {
  href: "/contact",
  label: "Contact"
}];
export function SiteNav() {
  const $ = _c(22);
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) {
    setLastPath(pathname);
    setMenuOpen(false);
  }
  let t0;
  let t1;
  if ($[0] !== menuOpen) {
    t0 = () => {
      if (!menuOpen) {
        return;
      }
      const handler = e => {
        if (e.key === "Escape") {
          setMenuOpen(false);
        }
      };
      document.addEventListener("keydown", handler);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handler);
        document.body.style.overflow = "";
      };
    };
    t1 = [menuOpen];
    $[0] = menuOpen;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Link href="/" className="group flex items-center gap-2"><img src="/Plample_logo.svg" alt="" width={28} height={28} className="size-7 object-contain transition-transform group-hover:rotate-3" /><span className="text-sm font-semibold tracking-tight">Plample</span></Link>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== pathname) {
    t3 = NAV_LINKS.map(link => <Link key={link.href} href={link.href} className={`transition-colors hover:text-foreground ${pathname === link.href ? "text-foreground" : ""}`}>{link.label}</Link>);
    $[4] = pathname;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="hidden items-center gap-7 text-sm text-muted-foreground lg:flex">{t3}</div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Link href="/recommend" className="inline-flex h-9 items-center rounded-full bg-foreground px-4 text-xs font-semibold text-background ring-1 ring-foreground transition-all hover:opacity-90 active:scale-95">Start search</Link>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setMenuOpen(true);
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Menu className="size-4" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== menuOpen) {
    t8 = <div className="flex items-center gap-2">{t5}<button type="button" onClick={t6} aria-label="Open menu" aria-expanded={menuOpen} className="inline-flex size-9 items-center justify-center rounded-full text-muted-foreground ring-1 ring-border/60 transition-colors hover:text-foreground active:scale-95 lg:hidden">{t7}</button></div>;
    $[11] = menuOpen;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t4 || $[14] !== t8) {
    t9 = <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-5 sm:px-8">{t2}{t4}{t8}</div>;
    $[13] = t4;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== menuOpen || $[17] !== pathname) {
    t10 = menuOpen && createPortal(<div className="fixed inset-0 z-[60] lg:hidden"><button type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)} className="absolute inset-0 bg-background/70 backdrop-blur-sm" /><div className="absolute inset-y-0 right-0 flex w-72 max-w-[80vw] flex-col bg-card shadow-2xl ring-1 ring-border/60"><div className="flex h-14 items-center justify-between border-b border-border/60 px-5"><span className="text-sm font-semibold tracking-tight">Menu</span><button type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="inline-flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground active:scale-95"><X className="size-4" /></button></div><div className="flex flex-col gap-1 overflow-y-auto p-3">{NAV_LINKS.map(link_0 => <Link key={link_0.href} href={link_0.href} onClick={() => setMenuOpen(false)} className={`rounded-xl px-3 py-3 text-sm transition-colors hover:bg-muted ${pathname === link_0.href ? "bg-muted text-foreground" : "text-muted-foreground"}`}>{link_0.label}</Link>)}</div></div></div>, document.body);
    $[16] = menuOpen;
    $[17] = pathname;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <nav className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-md">{t9}{t10}</nav>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
}